import React from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Clock,
  BookOpen,
  Award,
  MapPin,
  Briefcase,
  Star,
} from 'lucide-react';
import {
  StatsCard,
  QuickActions,
  UpcomingDeadlines,
  ProgressOverview,
} from '../components/dashboard';
import { useAuth } from '../contexts/AuthContext';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const stats = [
    {
      title: 'Active Goals',
      value: 8,
      icon: Target,
      trend: { value: 12.5, isPositive: true },
      color: 'primary' as const,
    },
    {
      title: 'Hours Logged',
      value: 127,
      icon: Clock,
      trend: { value: 8.3, isPositive: true },
      color: 'secondary' as const,
    },
    {
      title: 'GPA',
      value: '3.7',
      icon: BookOpen,
      trend: { value: 2.1, isPositive: true },
      color: 'accent' as const,
    },
    {
      title: 'Achievements',
      value: 15,
      icon: Award,
      trend: { value: 25.0, isPositive: true },
      color: 'primary' as const,
    },
    {
      title: 'Colleges Saved',
      value: 12,
      icon: MapPin,
      color: 'secondary' as const,
    },
    {
      title: 'Portfolio Items',
      value: 23,
      icon: Briefcase,
      trend: { value: 15.2, isPositive: true },
      color: 'accent' as const,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center lg:text-left"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.name?.split(' ')[0] || 'Student'}! 🌟
        </h1>
        <p className="text-lg text-gray-600">
          You're making great progress on your college journey. Keep it up!
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <StatsCard {...stat} />
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <QuickActions />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <ProgressOverview />
          </motion.div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <UpcomingDeadlines />
          </motion.div>

          {/* Motivational Quote */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl p-6 text-white"
          >
            <div className="flex items-center mb-3">
              <Star className="w-5 h-5 mr-2" />
              <span className="font-semibold">Daily Inspiration</span>
            </div>
            <p className="text-sm leading-relaxed opacity-90">
              "Success is not final, failure is not fatal: it is the courage to continue that counts."
            </p>
            <p className="text-xs mt-2 opacity-75">— Winston Churchill</p>
          </motion.div>
        </div>
      </div>

      {/* Success Metrics Section */}
    </div>
  );
};